import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import PeopleIcon from '@mui/icons-material/People';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SettingsIcon from '@mui/icons-material/Settings';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import SaveIcon from '@mui/icons-material/Save';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import QueueMusicIcon from '@mui/icons-material/QueueMusic';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import trainingApi from '../../services/trainingApi';
import { useTraining } from '../../context/TrainingContext';
import TrainingAudioExportQueue from '../../components/training/TrainingAudioExportQueue';

// --- Evaluation Methodology Manager ---
const EvaluationMethodologyManager = () => {
  const [methodologies, setMethodologies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [editSection, setEditSection] = useState('prompt'); // prompt | dimensions | visibility | advanced

  // Wizard state
  const [wizardMode, setWizardMode] = useState(false);
  const [wizardSessionId, setWizardSessionId] = useState(null);
  const [wizardMessages, setWizardMessages] = useState([]);
  const [wizardInput, setWizardInput] = useState('');
  const [wizardSending, setWizardSending] = useState(false);
  const [wizardConfig, setWizardConfig] = useState(null);
  const [wizardApplying, setWizardApplying] = useState(false);
  const wizardEndRef = useRef(null);

  // Document attachment state
  const [attachmentText, setAttachmentText] = useState('');
  const [attachmentName, setAttachmentName] = useState('');
  const fileInputRef = useRef(null);

  const loadMethodologies = useCallback(async () => {
    try {
      const res = await trainingApi.listMethodologies();
      const body = res.data || res;
      const data = body.data || body;
      setMethodologies(data.methodologies || data || []);
    } catch (err) {
      console.error('[MethodologyManager] Load error:', err);
      setError('Failed to load evaluation methodologies.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadMethodologies(); }, [loadMethodologies]);

  const handleSelect = useCallback(async (id) => {
    try {
      setError(null);
      const res = await trainingApi.getMethodology(id);
      const body = res.data || res;
      setSelected(body.data || body);
      setEditSection('prompt');
    } catch (err) {
      setError('Failed to load methodology details.');
    }
  }, []);

  const handleSave = useCallback(async () => {
    if (!selected) return;
    try {
      setIsSaving(true);
      setError(null);
      setSaveSuccess(false);
      const { _id, __v, createdAt, updatedAt, createdBy, updatedBy, ...updateData } = selected;
      const res = await trainingApi.updateMethodology(_id, updateData);
      const body = res.data || res;
      setSelected(body.data || body);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      loadMethodologies();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  }, [selected, loadMethodologies]);

  const updateField = (path, value) => {
    setSelected(prev => {
      const updated = { ...prev };
      const parts = path.split('.');
      let target = updated;
      for (let i = 0; i < parts.length - 1; i++) {
        target[parts[i]] = { ...target[parts[i]] };
        target = target[parts[i]];
      }
      target[parts[parts.length - 1]] = value;
      return updated;
    });
  };

  const updateDimension = (index, field, value) => {
    setSelected(prev => {
      const dims = [...(prev.dimensions || [])];
      dims[index] = { ...dims[index], [field]: value };
      return { ...prev, dimensions: dims };
    });
  };

  const addDimension = () => {
    setSelected(prev => ({
      ...prev,
      dimensions: [...(prev.dimensions || []), {
        key: `new_dimension_${Date.now()}`,
        name: 'New Dimension',
        description: '',
        weight: 1.0,
        maxScore: 10,
        rubric: [],
        criticalFailCriteria: null,
      }],
    }));
  };

  const removeDimension = (index) => {
    setSelected(prev => ({
      ...prev,
      dimensions: prev.dimensions.filter((_, i) => i !== index),
    }));
  };

  // --- Clone ---
  const handleClone = useCallback(async (id, e) => {
    if (e) e.stopPropagation();
    try {
      setError(null);
      const res = await trainingApi.getMethodology(id);
      const body = res.data || res;
      const source = body.data || body;
      const { _id, __v, createdAt, updatedAt, createdBy, updatedBy, ...cloneData } = source;
      const cloned = await trainingApi.createMethodology({
        ...cloneData,
        key: `${source.key}-copy-${Date.now().toString(36)}`,
        name: `${source.name} (Copy)`,
        isActive: false,
        version: 1,
      });
      const clonedBody = cloned.data || cloned;
      const created = clonedBody.data || clonedBody;
      loadMethodologies();
      handleSelect(created._id);
    } catch (err) {
      setError(err.message || 'Failed to clone methodology.');
    }
  }, [loadMethodologies, handleSelect]);

  // --- Wizard ---
  const startWizard = useCallback(async () => {
    try {
      setError(null);
      const res = await trainingApi.startMethodologyWizard();
      const body = res.data || res;
      const data = body.data || body;
      setWizardSessionId(data.sessionId);
      setWizardMessages([{ role: 'wizard', content: data.text }]);
      setWizardConfig(null);
      setWizardMode(true);
    } catch (err) {
      setError('Failed to start wizard.');
    }
  }, []);

  const sendWizardMessage = useCallback(async () => {
    if (!wizardInput.trim() && !attachmentText) return;
    const msg = wizardInput.trim();
    setWizardInput('');
    setWizardSending(true);

    setWizardMessages(prev => [...prev, { role: 'user', content: msg + (attachmentText ? `\n[Attached: ${attachmentName}]` : '') }]);

    try {
      const res = await trainingApi.sendMethodologyWizardMessage(wizardSessionId, msg, attachmentText || undefined);
      const body = res.data || res;
      const data = body.data || body;

      // Strip JSON blocks from displayed text for cleaner UX
      const displayText = (data.text || '').replace(/```json[\s\S]*?```/g, '[Methodology configuration generated — see preview below]');
      setWizardMessages(prev => [...prev, { role: 'wizard', content: displayText }]);

      if (data.hasConfig && data.config) {
        setWizardConfig(data.config);
      }
      setAttachmentText('');
      setAttachmentName('');
    } catch (err) {
      setWizardMessages(prev => [...prev, { role: 'wizard', content: 'Sorry, something went wrong. Please try again.' }]);
    } finally {
      setWizardSending(false);
    }
  }, [wizardInput, wizardSessionId, attachmentText, attachmentName]);

  const applyWizardConfig = useCallback(async () => {
    try {
      setWizardApplying(true);
      setError(null);
      const res = await trainingApi.applyMethodologyWizard(wizardSessionId);
      const body = res.data || res;
      const created = body.data || body;
      setWizardMode(false);
      setWizardSessionId(null);
      setWizardMessages([]);
      setWizardConfig(null);
      loadMethodologies();
      handleSelect(created._id);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to apply methodology.');
    } finally {
      setWizardApplying(false);
    }
  }, [wizardSessionId, loadMethodologies, handleSelect]);

  const handleFileUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setAttachmentText(ev.target.result);
      setAttachmentName(file.name);
    };
    reader.readAsText(file);
    e.target.value = '';
  }, []);

  // --- Export / Import ---
  const importFileRef = useRef(null);

  const handleExport = useCallback(async (id, e) => {
    if (e) e.stopPropagation();
    try {
      setError(null);
      const response = await trainingApi.exportMethodology(id);
      const blob = response.data;
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      const disposition = response.headers?.['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
      link.download = filenameMatch?.[1] || 'methodology-export.json';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setError('Failed to export methodology.');
    }
  }, []);

  const handleImportFile = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    try {
      setError(null);
      const text = await file.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        setError('Invalid JSON file. Please select a valid methodology export.');
        return;
      }
      const res = await trainingApi.importMethodology(data);
      const body = res.data || res;
      const created = body.data || body;
      loadMethodologies();
      handleSelect(created._id);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to import methodology.');
    }
  }, [loadMethodologies, handleSelect]);

  // Auto-scroll wizard chat
  useEffect(() => {
    if (wizardEndRef.current) wizardEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [wizardMessages]);

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>;
  }

  // Wizard view
  if (wizardMode) {
    return (
      <Box sx={{ p: 3, display: 'flex', gap: 2, height: 600 }}>
        {/* Chat panel */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>Methodology Wizard</Typography>
            <Button size="small" onClick={() => setWizardMode(false)}>Cancel</Button>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', mb: 2, bgcolor: 'grey.50', borderRadius: 2, p: 2 }}>
            {wizardMessages.map((msg, i) => (
              <Box key={i} sx={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', mb: 1.5 }}>
                <Box sx={{ maxWidth: '80%' }}>
                  <Typography variant="caption" color="text.secondary">{msg.role === 'user' ? 'You' : 'Wizard'}</Typography>
                  <Box sx={{
                    bgcolor: msg.role === 'user' ? 'primary.main' : 'background.paper',
                    color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
                    borderRadius: 2, px: 2, py: 1.5, border: msg.role === 'user' ? 0 : 1, borderColor: 'divider',
                  }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{msg.content}</Typography>
                  </Box>
                </Box>
              </Box>
            ))}
            {wizardSending && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1.5 }}>
                <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, px: 2, py: 1.5, border: 1, borderColor: 'divider' }}>
                  <CircularProgress size={16} sx={{ mr: 1 }} /><Typography variant="body2" component="span">Thinking...</Typography>
                </Box>
              </Box>
            )}
            <div ref={wizardEndRef} />
          </Box>
          {/* Attachment indicator */}
          {attachmentName && (
            <Chip label={`Attached: ${attachmentName}`} onDelete={() => { setAttachmentText(''); setAttachmentName(''); }} size="small" sx={{ mb: 1, alignSelf: 'flex-start' }} />
          )}
          {/* Input */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept=".txt,.md,.pdf,.csv,.json" onChange={handleFileUpload} />
            <Button size="small" variant="outlined" onClick={() => fileInputRef.current?.click()} sx={{ minWidth: 'auto', px: 1 }}>
              Attach
            </Button>
            <TextField
              size="small" fullWidth placeholder="Describe your evaluation needs..."
              value={wizardInput} onChange={e => setWizardInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendWizardMessage(); } }}
              disabled={wizardSending} multiline maxRows={3}
            />
            <Button variant="contained" onClick={sendWizardMessage} disabled={wizardSending || (!wizardInput.trim() && !attachmentText)}>
              Send
            </Button>
          </Box>
        </Box>
        {/* Config preview panel */}
        {wizardConfig && (
          <Box sx={{ width: 320, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Generated Methodology</Typography>
            <Paper variant="outlined" sx={{ flex: 1, overflowY: 'auto', p: 2, mb: 2 }}>
              <Typography variant="body2" fontWeight={600}>{wizardConfig.name}</Typography>
              <Typography variant="caption" color="text.secondary" display="block">{wizardConfig.key}</Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>{wizardConfig.organisation}</Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" fontWeight={600}>Dimensions ({wizardConfig.dimensions?.length || 0}):</Typography>
              {(wizardConfig.dimensions || []).map((d, i) => (
                <Typography key={i} variant="caption" display="block">
                  {d.name} (w: {d.weight})
                </Typography>
              ))}
              {wizardConfig.passingScore && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>Pass threshold: {wizardConfig.passingScore}%</Typography>
              )}
            </Paper>
            <Button variant="contained" color="success" onClick={applyWizardConfig} disabled={wizardApplying} fullWidth>
              {wizardApplying ? 'Creating...' : 'Create Methodology'}
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  // List view
  if (!selected) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>Evaluation Methodologies</Typography>
            <Typography variant="body2" color="text.secondary">
              Define how student sessions are scored. Different methodologies support different evaluation perspectives.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <input type="file" ref={importFileRef} style={{ display: 'none' }} accept=".json" onChange={handleImportFile} />
            <Button variant="outlined" size="small" startIcon={<FileUploadIcon />} onClick={() => importFileRef.current?.click()}>
              Import
            </Button>
            <Button variant="contained" size="small" onClick={startWizard}>
              Create with Wizard
            </Button>
          </Box>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {methodologies.length === 0 ? (
          <Alert severity="info">No evaluation methodologies found. Click "Create with Wizard" to build your first one.</Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><Typography variant="subtitle2" fontWeight={600}>Name</Typography></TableCell>
                <TableCell><Typography variant="subtitle2" fontWeight={600}>Organisation</Typography></TableCell>
                <TableCell align="center"><Typography variant="subtitle2" fontWeight={600}>Dimensions</Typography></TableCell>
                <TableCell align="center"><Typography variant="subtitle2" fontWeight={600}>Version</Typography></TableCell>
                <TableCell align="center"><Typography variant="subtitle2" fontWeight={600}>Active</Typography></TableCell>
                <TableCell align="center"><Typography variant="subtitle2" fontWeight={600}>Actions</Typography></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {methodologies.map(m => (
                <TableRow key={m._id} hover sx={{ cursor: 'pointer' }} onClick={() => handleSelect(m._id)}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{m.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{m.key}</Typography>
                  </TableCell>
                  <TableCell><Typography variant="body2">{m.organisation || '—'}</Typography></TableCell>
                  <TableCell align="center"><Typography variant="body2">{m.dimensions?.length || 0}</Typography></TableCell>
                  <TableCell align="center"><Chip label={`v${m.version}`} size="small" variant="outlined" /></TableCell>
                  <TableCell align="center">
                    <Chip label={m.isActive ? 'Active' : 'Inactive'} size="small" color={m.isActive ? 'success' : 'default'} variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Export as JSON"><IconButton size="small" onClick={(e) => handleExport(m._id, e)}><FileDownloadIcon fontSize="small" /></IconButton></Tooltip>
                    <Button size="small" onClick={(e) => handleClone(m._id, e)}>Clone</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
    );
  }

  // Detail/edit view
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button size="small" onClick={() => setSelected(null)}>Back</Button>
          <Typography variant="h6" fontWeight={600}>{selected.name}</Typography>
          <Chip label={`v${selected.version}`} size="small" variant="outlined" />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" startIcon={<FileDownloadIcon />} onClick={(e) => handleExport(selected._id, e)}>
            Export
          </Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {saveSuccess && <Alert severity="success" sx={{ mb: 2 }}>Methodology saved. Version incremented.</Alert>}

      {/* Section nav */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
        {['prompt', 'dimensions', 'visibility', 'advanced'].map(s => (
          <Chip
            key={s}
            label={{ prompt: 'Reviewer Prompt', dimensions: 'Dimensions', visibility: 'Visibility Calibration', advanced: 'Advanced' }[s]}
            onClick={() => setEditSection(s)}
            color={editSection === s ? 'primary' : 'default'}
            variant={editSection === s ? 'filled' : 'outlined'}
          />
        ))}
      </Box>

      {/* Metadata (always visible) */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField size="small" label="Name" value={selected.name || ''} onChange={e => updateField('name', e.target.value)} sx={{ minWidth: 250 }} />
        <TextField size="small" label="Organisation" value={selected.organisation || ''} onChange={e => updateField('organisation', e.target.value)} sx={{ minWidth: 200 }} />
        <TextField size="small" label="Passing Score (%)" type="number" value={selected.passingScore ?? ''} onChange={e => updateField('passingScore', e.target.value ? Number(e.target.value) : null)} sx={{ width: 140 }} />
        <FormControlLabel
          control={<Switch checked={selected.isActive !== false} onChange={e => updateField('isActive', e.target.checked)} />}
          label="Active"
        />
      </Box>

      {/* Reviewer Prompt */}
      {editSection === 'prompt' && (
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>Reviewer System Prompt</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            This defines the reviewer's identity, qualifications, and evaluation approach.
          </Typography>
          <TextField
            multiline rows={20} fullWidth
            value={selected.reviewerSystemPrompt || ''}
            onChange={e => updateField('reviewerSystemPrompt', e.target.value)}
            InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
          />
        </Box>
      )}

      {/* Dimensions */}
      {editSection === 'dimensions' && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600}>Evaluation Dimensions ({(selected.dimensions || []).length})</Typography>
            <Button size="small" variant="outlined" onClick={addDimension}>Add Dimension</Button>
          </Box>
          {(selected.dimensions || []).map((dim, i) => (
            <Paper key={dim.key || i} variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField size="small" label="Key" value={dim.key || ''} onChange={e => updateDimension(i, 'key', e.target.value)} sx={{ width: 180 }} />
                <TextField size="small" label="Name" value={dim.name || ''} onChange={e => updateDimension(i, 'name', e.target.value)} sx={{ flex: 1, minWidth: 200 }} />
                <TextField size="small" label="Weight" type="number" value={dim.weight ?? 1} onChange={e => updateDimension(i, 'weight', Number(e.target.value))} sx={{ width: 80 }} inputProps={{ step: 0.1, min: 0.1, max: 5 }} />
                <TextField size="small" label="Max" type="number" value={dim.maxScore ?? 10} onChange={e => updateDimension(i, 'maxScore', Number(e.target.value))} sx={{ width: 70 }} />
                <Button size="small" color="error" onClick={() => removeDimension(i)}>Remove</Button>
              </Box>
              <TextField size="small" label="Description" fullWidth multiline rows={2} value={dim.description || ''} onChange={e => updateDimension(i, 'description', e.target.value)} sx={{ mb: 1 }} />
              <TextField size="small" label="Critical Fail Criteria (optional)" fullWidth value={dim.criticalFailCriteria || ''} onChange={e => updateDimension(i, 'criticalFailCriteria', e.target.value || null)} />
            </Paper>
          ))}
        </Box>
      )}

      {/* Visibility Calibration */}
      {editSection === 'visibility' && (
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>Visibility-Aware Scoring Calibration</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Adjust evaluation expectations based on how much information the student was given before the session.
          </Typography>
          {['blind', 'referral_only', 'full_brief'].map(level => {
            const cal = selected.visibilityCalibration?.[level] || {};
            return (
              <Paper key={level} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, textTransform: 'capitalize' }}>
                  {level.replace(/_/g, ' ')}
                  <Chip label={cal.scoringModifier || 'standard'} size="small" sx={{ ml: 1 }}
                    color={cal.scoringModifier === 'generous' ? 'success' : cal.scoringModifier === 'rigorous' ? 'warning' : 'default'} />
                </Typography>
                <TextField
                  size="small" select label="Scoring Modifier" value={cal.scoringModifier || 'standard'}
                  onChange={e => updateField(`visibilityCalibration.${level}.scoringModifier`, e.target.value)}
                  sx={{ width: 160, mb: 1, mr: 2 }}
                >
                  <MenuItem value="generous">Generous</MenuItem>
                  <MenuItem value="standard">Standard</MenuItem>
                  <MenuItem value="rigorous">Rigorous</MenuItem>
                </TextField>
                <TextField
                  size="small" label="Guidance for Reviewer" fullWidth multiline rows={3}
                  value={cal.guidance || ''}
                  onChange={e => updateField(`visibilityCalibration.${level}.guidance`, e.target.value)}
                />
              </Paper>
            );
          })}
        </Box>
      )}

      {/* Advanced */}
      {editSection === 'advanced' && (
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>Narrative Tone Guidance</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Instructions ensuring the written narrative aligns with numerical scores.
          </Typography>
          <TextField
            multiline rows={8} fullWidth
            value={selected.narrativeToneGuidance || ''}
            onChange={e => updateField('narrativeToneGuidance', e.target.value)}
            InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
            sx={{ mb: 3 }}
          />

          <Typography variant="subtitle2" fontWeight={600} gutterBottom>Formulation Config</Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField size="small" label="Interview Weight" type="number" value={selected.formulationConfig?.interviewWeight ?? 0.65}
              onChange={e => updateField('formulationConfig.interviewWeight', Number(e.target.value))} sx={{ width: 150 }} inputProps={{ step: 0.05, min: 0, max: 1 }} />
            <TextField size="small" label="Formulation Weight" type="number" value={selected.formulationConfig?.formulationWeight ?? 0.35}
              onChange={e => updateField('formulationConfig.formulationWeight', Number(e.target.value))} sx={{ width: 150 }} inputProps={{ step: 0.05, min: 0, max: 1 }} />
          </Box>

          <Typography variant="subtitle2" fontWeight={600} gutterBottom>Description</Typography>
          <TextField multiline rows={3} fullWidth value={selected.description || ''} onChange={e => updateField('description', e.target.value)} />
        </Box>
      )}
    </Box>
  );
};

const TrainingVoiceSettings = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const token = localStorage.getItem('token');

  useEffect(() => {
    trainingApi.getVoiceConfig()
      .then(res => { setConfig((res.data || res)?.data || { enabled: false }); })
      .catch(err => setError('Failed to load voice settings'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await trainingApi.updateVoiceConfig(config);
      setSuccess('Voice settings saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to save voice settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>;

  const VoiceSelectorCommon = React.lazy(() => import('../../components/common/VoiceSelector'));
  const AccentSelectorCommon = React.lazy(() => import('../../components/common/AccentSelector'));

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      <Typography variant="h6" gutterBottom>Training Voice Mode Settings</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure Advanced Voice Mode for training simulations. When enabled, students can have
        spoken conversations with simulated patients. These are platform-wide defaults —
        individual scenarios can override the voice and accent.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Kill Switch */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, border: config?.enabled ? '2px solid' : '1px solid', borderColor: config?.enabled ? 'success.main' : 'divider' }}>
        <FormControlLabel
          control={<Switch checked={config?.enabled || false} onChange={(e) => setConfig({ ...config, enabled: e.target.checked })} color="success" />}
          label={
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                {config?.enabled ? 'Training Voice Mode ENABLED' : 'Training Voice Mode DISABLED'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                When enabled, scenarios with voice mode can offer spoken patient conversations
              </Typography>
            </Box>
          }
        />
      </Paper>

      {config?.enabled && (
        <>
          {/* Accent */}
          <Typography variant="subtitle2" gutterBottom>Default Accent / Regional Style</Typography>
          <React.Suspense fallback={<CircularProgress size={20} />}>
            <AccentSelectorCommon
              value={config.accentPresetId || null}
              onChange={(presetId) => setConfig({ ...config, accentPresetId: presetId })}
              customGuidance={config.accentCustomGuidance || ''}
              onCustomGuidanceChange={(text) => setConfig({ ...config, accentCustomGuidance: text })}
              token={token}
            />
          </React.Suspense>

          {/* Voice */}
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 3 }}>Default Patient Voice</Typography>
          <React.Suspense fallback={<CircularProgress size={20} />}>
            <VoiceSelectorCommon
              value={config.geminiLive?.voicePreset || 'Sulafat'}
              onChange={(voice) => setConfig({ ...config, geminiLive: { ...(config.geminiLive || {}), voicePreset: voice || 'Sulafat' } })}
              token={token}
              accentPresetId={config.accentPresetId}
            />
          </React.Suspense>
        </>
      )}

      <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ mt: 3 }}>
        {saving ? <CircularProgress size={20} /> : 'Save Voice Settings'}
      </Button>
    </Box>
  );
};

const formatDate = (dateStr) => {
  if (!dateStr) return '--';
  return new Date(dateStr).toLocaleDateString('en-IE', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '--';
  return new Date(dateStr).toLocaleDateString('en-IE', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const getStatusColor = (status) => {
  switch (status) {
    case 'completed': return 'success';
    case 'evaluated': return 'info';
    case 'in_progress': return 'warning';
    case 'abandoned': return 'error';
    default: return 'default';
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case 'completed': return 'Completed';
    case 'evaluated': return 'Evaluated';
    case 'in_progress': return 'In Progress';
    case 'abandoned': return 'Abandoned';
    default: return status || '--';
  }
};

// Stat card for overview panel
const StatCard = ({ icon, label, value, color = 'primary' }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2.5,
      flex: 1,
      minWidth: 180,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
    }}
  >
    <Box
      sx={{
        bgcolor: `${color}.light`,
        color: `${color}.dark`,
        borderRadius: 2,
        p: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography variant="h4" fontWeight={700}>{value}</Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Box>
  </Paper>
);

// --- Lecturer Assignments Tab ---
const LecturerAssignmentsTab = ({ cohorts, navigate }) => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const [form, setForm] = useState({ title: '', description: '', cohortId: '', scenarioIds: [], mode: 'assessment', dueDate: '', maxAttempts: 1 });
  const [saving, setSaving] = useState(false);

  const loadAssignments = useCallback(async () => {
    try {
      setLoading(true);
      const res = await trainingApi.listAssignments();
      const body = res.data || res;
      const data = body.data || body;
      setAssignments(data.assignments || data || []);
    } catch (err) {
      setError('Failed to load assignments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAssignments(); }, [loadAssignments]);

  const openCreate = async () => {
    try {
      const res = await trainingApi.listScenarios({ status: 'published', limit: 100 });
      const body = res.data || res;
      const data = body.data || body;
      setScenarios(data.scenarios || data || []);
    } catch { /* ignore */ }
    setForm({ title: '', description: '', cohortId: cohorts[0]?._id || '', scenarioIds: [], mode: 'assessment', dueDate: '', maxAttempts: 1 });
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    try {
      setSaving(true);
      setError(null);
      await trainingApi.createAssignment({
        ...form,
        status: 'published',
        dueDate: form.dueDate ? new Date(form.dueDate).toISOString() : undefined,
      });
      setCreateOpen(false);
      loadAssignments();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to create assignment');
    } finally {
      setSaving(false);
    }
  };

  const toggleScenario = (id) => {
    setForm(f => ({
      ...f,
      scenarioIds: f.scenarioIds.includes(id)
        ? f.scenarioIds.filter(s => s !== id)
        : [...f.scenarioIds, id],
    }));
  };

  if (loading) return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>;

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>Assignments</Typography>
        <Button variant="contained" size="small" onClick={openCreate}>Create Assignment</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {assignments.length === 0 ? (
        <Alert severity="info">No assignments yet. Create one to assign scenarios to your cohorts.</Alert>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><Typography variant="subtitle2" fontWeight={600}>Title</Typography></TableCell>
              <TableCell><Typography variant="subtitle2" fontWeight={600}>Cohort</Typography></TableCell>
              <TableCell><Typography variant="subtitle2" fontWeight={600}>Scenarios</Typography></TableCell>
              <TableCell><Typography variant="subtitle2" fontWeight={600}>Mode</Typography></TableCell>
              <TableCell><Typography variant="subtitle2" fontWeight={600}>Due Date</Typography></TableCell>
              <TableCell align="center"><Typography variant="subtitle2" fontWeight={600}>Status</Typography></TableCell>
              <TableCell align="center"><Typography variant="subtitle2" fontWeight={600}>Submissions</Typography></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assignments.map(a => (
              <TableRow key={a._id} hover>
                <TableCell><Typography variant="body2" fontWeight={500}>{a.title}</Typography></TableCell>
                <TableCell>{a.cohortId?.name || '—'}</TableCell>
                <TableCell>{(a.scenarioIds || []).map(s => s.name || 'Scenario').join(', ') || '—'}</TableCell>
                <TableCell><Chip size="small" label={a.mode} variant="outlined" /></TableCell>
                <TableCell>{a.dueDate ? new Date(a.dueDate).toLocaleDateString() : '—'}</TableCell>
                <TableCell align="center">
                  <TextField
                    select size="small" variant="standard"
                    value={a.status || 'draft'}
                    onChange={async (e) => {
                      try {
                        await trainingApi.updateAssignment(a._id, { status: e.target.value });
                        loadAssignments();
                      } catch { /* ignore */ }
                    }}
                    sx={{ minWidth: 100 }}
                  >
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="published">Published</MenuItem>
                    <MenuItem value="closed">Closed</MenuItem>
                  </TextField>
                </TableCell>
                <TableCell align="center">{a.stats?.completedCount ?? 0} / {a.stats?.totalStudents ?? 0}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Create Assignment Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Assignment</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Title" fullWidth required value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
            <TextField label="Description" fullWidth multiline rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            <TextField label="Cohort" fullWidth select required value={form.cohortId} onChange={e => setForm(f => ({ ...f, cohortId: e.target.value }))}>
              {cohorts.map(c => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
            </TextField>
            <Typography variant="subtitle2">Scenarios</Typography>
            <Box sx={{ maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
              {scenarios.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No published scenarios available</Typography>
              ) : scenarios.map(s => (
                <Box key={s._id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                  <Switch size="small" checked={form.scenarioIds.includes(s._id)} onChange={() => toggleScenario(s._id)} />
                  <Typography variant="body2">{s.name}</Typography>
                  <Chip size="small" label={s.difficulty || 'unknown'} variant="outlined" sx={{ ml: 'auto' }} />
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Mode" select value={form.mode} onChange={e => setForm(f => ({ ...f, mode: e.target.value }))} sx={{ flex: 1 }}>
                <MenuItem value="practice">Practice</MenuItem>
                <MenuItem value="assessment">Assessment (Graded)</MenuItem>
              </TextField>
              <TextField label="Max Attempts" type="number" value={form.maxAttempts} onChange={e => setForm(f => ({ ...f, maxAttempts: parseInt(e.target.value) || 1 }))} sx={{ width: 130 }} inputProps={{ min: 1 }} />
            </Box>
            <TextField label="Due Date" type="date" value={form.dueDate} onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))} InputLabelProps={{ shrink: true }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving || !form.title.trim() || !form.cohortId || form.scenarioIds.length === 0}>
            {saving ? 'Creating...' : 'Create & Publish'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const LecturerDashboard = () => {
  const navigate = useNavigate();
  const { user } = useTraining();

  const [activeTab, setActiveTab] = useState('students');
  const [sessions, setSessions] = useState([]);
  const [students, setStudents] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Cohort state
  const [cohorts, setCohorts] = useState([]);
  const [cohortFilter, setCohortFilter] = useState('');

  // Filter state
  const [scenarioFilter, setScenarioFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [studentFilter, setStudentFilter] = useState(null); // { id, name }

  // Pagination state (server-side for sessions)
  const [sessionsPage, setSessionsPage] = useState(0); // 0-based for MUI
  const [sessionsPerPage, setSessionsPerPage] = useState(20);
  const [sessionsPagination, setSessionsPagination] = useState({ total: 0, pages: 0 });

  // Sort state
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');

  // Initial load: students + stats (once)
  useEffect(() => {
    const fetchInitial = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const [studentsRes, statsRes, cohortsRes] = await Promise.all([
          trainingApi.lecturerListStudents().catch(() => ({ data: { students: [] } })),
          trainingApi.lecturerGetStats().catch(() => ({ data: null })),
          trainingApi.listCohorts().catch(() => ({ data: { cohorts: [] } })),
        ]);

        const _raw_studentsData = studentsRes.data || studentsRes; const studentsData = _raw_studentsData.data || _raw_studentsData;
        setStudents(studentsData.students || studentsData || []);

        const _raw_statsData = statsRes.data || statsRes; const statsData = _raw_statsData.data || _raw_statsData;
        setStats(statsData);

        const _raw_cohortsData = cohortsRes.data || cohortsRes; const cohortsData = _raw_cohortsData.data || _raw_cohortsData;
        setCohorts(cohortsData.cohorts || cohortsData || []);
      } catch (err) {
        console.error('[LecturerDashboard] Error loading data:', err);
        setError('Failed to load dashboard data. The training API may not be available yet.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitial();
  }, []);

  // Fetch sessions with server-side pagination and filtering
  const fetchSessions = useCallback(async () => {
    try {
      const params = {
        page: sessionsPage + 1, // API is 1-based
        limit: sessionsPerPage,
      };
      if (studentFilter?.id) params.studentId = studentFilter.id;
      if (statusFilter) params.status = statusFilter;
      if (cohortFilter) params.cohortId = cohortFilter;

      const sessionsRes = await trainingApi.lecturerListSessions(params).catch(() => ({ data: { sessions: [] } }));
      const _raw = sessionsRes.data || sessionsRes; const sessionsData = _raw.data || _raw;
      setSessions(sessionsData.sessions || sessionsData || []);
      if (sessionsData.pagination) {
        setSessionsPagination({
          total: sessionsData.pagination.total || 0,
          pages: sessionsData.pagination.pages || 0,
        });
      }
    } catch (err) {
      console.error('[LecturerDashboard] Error loading sessions:', err);
    }
  }, [sessionsPage, sessionsPerPage, studentFilter, statusFilter, cohortFilter]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Compute stats from data if the stats endpoint isn't available
  const computedStats = useMemo(() => {
    if (stats && stats.totalStudents != null) return stats;

    const uniqueStudents = new Set(sessions.map(s => s.studentId || s.userId)).size;
    const completedSessions = sessions.filter(s => s.status === 'completed' || s.status === 'evaluated').length;
    const pendingEvaluations = sessions.filter(s => s.status === 'completed' && !s.evaluationId).length;

    const scoredSessions = sessions.filter(s => s.compositeScore != null);
    const avgScore = scoredSessions.length > 0
      ? Math.round(scoredSessions.reduce((sum, s) => sum + s.compositeScore, 0) / scoredSessions.length)
      : null;

    return {
      totalStudents: students.length || uniqueStudents,
      totalSessions: completedSessions,
      pendingEvaluations,
      averageScore: avgScore,
    };
  }, [stats, sessions, students]);

  // Get unique scenario names for filter
  const scenarioOptions = useMemo(() => {
    const names = [...new Set(sessions.map(s => s.scenarioName).filter(Boolean))];
    return names.sort();
  }, [sessions]);

  // Filter and sort sessions (studentId and status are now server-side)
  const filteredSessions = useMemo(() => {
    let result = [...sessions];

    if (scenarioFilter) {
      result = result.filter(s => s.scenarioName === scenarioFilter);
    }
    if (searchQuery && !studentFilter) {
      const q = searchQuery.toLowerCase();
      result = result.filter(s =>
        (s.studentName || '').toLowerCase().includes(q) ||
        (s.scenarioName || '').toLowerCase().includes(q)
      );
    }

    result.sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'student':
          aVal = (a.studentName || '').toLowerCase();
          bVal = (b.studentName || '').toLowerCase();
          break;
        case 'scenario':
          aVal = (a.scenarioName || '').toLowerCase();
          bVal = (b.scenarioName || '').toLowerCase();
          break;
        case 'score':
          aVal = a.compositeScore ?? -1;
          bVal = b.compositeScore ?? -1;
          break;
        case 'date':
        default:
          aVal = new Date(a.completedAt || a.startedAt || 0).getTime();
          bVal = new Date(b.completedAt || b.startedAt || 0).getTime();
          break;
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [sessions, scenarioFilter, searchQuery, studentFilter, sortField, sortDirection]);

  // Filter students by search
  const filteredStudents = useMemo(() => {
    if (!searchQuery) return students;
    const q = searchQuery.toLowerCase();
    return students.filter(s =>
      (s.name || '').toLowerCase().includes(q) ||
      (s.email || '').toLowerCase().includes(q)
    );
  }, [students, searchQuery]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleSessionClick = (sessionId) => {
    navigate(`/training/lecturer/session/${sessionId}`);
  };

  const handleStudentClick = (studentId) => {
    const student = students.find(s => (s.studentId || s._id) === studentId);
    setActiveTab('sessions');
    setStudentFilter({ id: studentId, name: student?.name || student?.email || 'Student' });
    setSearchQuery('');
    setSessionsPage(0);
  };

  const clearStudentFilter = () => {
    setStudentFilter(null);
    setSessionsPage(0);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>Loading lecturer dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* Page header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>Lecturer Dashboard</Typography>
        <Typography variant="body2" color="text.secondary">
          Monitor student training sessions and review evaluations
        </Typography>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>{error}</Alert>
      )}

      {/* Overview stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <StatCard
          icon={<PeopleIcon />}
          label="Total Students"
          value={computedStats.totalStudents ?? 0}
          color="primary"
        />
        <StatCard
          icon={<AssignmentIcon />}
          label="Sessions Completed"
          value={computedStats.totalSessions ?? 0}
          color="success"
        />
        <StatCard
          icon={<PendingActionsIcon />}
          label="Pending Evaluations"
          value={computedStats.pendingEvaluations ?? 0}
          color="warning"
        />
        <StatCard
          icon={<TrendingUpIcon />}
          label="Average Score"
          value={computedStats.averageScore != null ? `${computedStats.averageScore}%` : '--'}
          color="info"
        />
      </Box>

      {/* Tabs */}
      <Paper variant="outlined" sx={{ mb: 0 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => { setActiveTab(v); setSearchQuery(''); }}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab value="students" label={`Students (${students.length})`} />
          <Tab value="sessions" label={`All Sessions (${sessions.length})`} />
          <Tab value="cohorts" label={`My Cohorts (${cohorts.length})`} />
          <Tab value="assignments" label="Assignments" />
          <Tab value="reviewer" label="Evaluation Methods" icon={<SettingsIcon fontSize="small" />} iconPosition="start" />
          <Tab value="voice" label="Voice Settings" icon={<HeadsetMicIcon fontSize="small" />} iconPosition="start" />
          <Tab value="audioExports" label="Audio Exports" icon={<QueueMusicIcon fontSize="small" />} iconPosition="start" />
        </Tabs>

        {/* Filters bar (hidden on config tabs) */}
        {activeTab !== 'reviewer' && activeTab !== 'voice' && activeTab !== 'audioExports' && <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', bgcolor: 'grey.50' }}>
          {/* Active student filter chip */}
          {activeTab === 'sessions' && studentFilter && (
            <Chip
              label={`Student: ${studentFilter.name}`}
              onDelete={clearStudentFilter}
              color="primary"
              variant="outlined"
              size="small"
            />
          )}
          <TextField
            size="small"
            placeholder={activeTab === 'students' ? 'Search students...' : 'Search by student or scenario...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />
          {cohorts.length > 0 && (
            <TextField
              size="small" select label="Cohort" value={cohortFilter}
              onChange={(e) => { setCohortFilter(e.target.value); setSessionsPage(0); }}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">All Cohorts</MenuItem>
              {cohorts.map(c => (
                <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
              ))}
            </TextField>
          )}
          {activeTab === 'sessions' && (
            <>
              <TextField
                size="small"
                select
                label="Scenario"
                value={scenarioFilter}
                onChange={(e) => setScenarioFilter(e.target.value)}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="">All Scenarios</MenuItem>
                {scenarioOptions.map(name => (
                  <MenuItem key={name} value={name}>{name}</MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                select
                label="Status"
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setSessionsPage(0); }}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="evaluated">Evaluated</MenuItem>
                <MenuItem value="abandoned">Abandoned</MenuItem>
              </TextField>
            </>
          )}
        </Box>}

        {/* Students Tab */}
        {activeTab === 'students' && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Name</Typography></TableCell>
                  <TableCell align="center"><Typography variant="subtitle2" fontWeight={600}>Sessions</Typography></TableCell>
                  <TableCell align="center"><Typography variant="subtitle2" fontWeight={600}>Avg Score</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Last Active</Typography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredStudents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        {searchQuery ? 'No students match your search.' : 'No student data available yet.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredStudents.map((student) => (
                    <TableRow
                      key={student.studentId || student._id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleStudentClick(student.studentId || student._id)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {student.name || student.email || 'Unknown Student'}
                        </Typography>
                        {student.email && student.name && (
                          <Typography variant="caption" color="text.secondary">{student.email}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">{student.sessionCount ?? 0}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        {student.averageScore != null ? (
                          <Chip
                            label={`${Math.round(student.averageScore)}%`}
                            size="small"
                            color={student.averageScore >= 70 ? 'success' : student.averageScore >= 50 ? 'warning' : 'error'}
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">--</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatDate(student.lastActiveAt)}</Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Evaluation Methodology Tab */}
        {activeTab === 'reviewer' && (
          <EvaluationMethodologyManager />
        )}

        {/* Voice Settings Tab */}
        {activeTab === 'voice' && (
          <TrainingVoiceSettings />
        )}

        {/* Cohorts Tab */}
        {activeTab === 'cohorts' && (
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>My Cohorts</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Cohorts you are assigned to. Click to manage students, assignments, and view progress.
            </Typography>
            {cohorts.length === 0 ? (
              <Alert severity="info">
                You are not assigned to any cohorts yet. Your training administrator needs to assign you to a cohort
                via the Cohort Detail page &gt; Lecturers tab. Once assigned, you can manage students, create assignments,
                and view progress for your cohorts here.
              </Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><Typography variant="subtitle2" fontWeight={600}>Name</Typography></TableCell>
                    <TableCell><Typography variant="subtitle2" fontWeight={600}>Code</Typography></TableCell>
                    <TableCell><Typography variant="subtitle2" fontWeight={600}>Academic Year</Typography></TableCell>
                    <TableCell align="center"><Typography variant="subtitle2" fontWeight={600}>Students</Typography></TableCell>
                    <TableCell align="center"><Typography variant="subtitle2" fontWeight={600}>Your Role</Typography></TableCell>
                    <TableCell align="center"><Typography variant="subtitle2" fontWeight={600}>Status</Typography></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cohorts.map(c => {
                    const myEntry = c.lecturers?.find(l => (l.userId?._id || l.userId) === (user?._id || user?.id));
                    return (
                      <TableRow key={c._id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/training/admin/cohorts/${c._id}`)}>
                        <TableCell><Typography variant="body2" fontWeight={500}>{c.name}</Typography></TableCell>
                        <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{c.code || '—'}</Typography></TableCell>
                        <TableCell>{c.academicYear || '—'}</TableCell>
                        <TableCell align="center">{c.students?.filter(s => s.status === 'active').length ?? 0}</TableCell>
                        <TableCell align="center"><Chip size="small" label={myEntry?.role || 'lecturer'} variant="outlined" /></TableCell>
                        <TableCell align="center"><Chip size="small" label={c.status || 'active'} color={c.status === 'active' ? 'success' : 'default'} variant="outlined" /></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Box>
        )}

        {/* Assignments Tab */}
        {activeTab === 'assignments' && (
          <LecturerAssignmentsTab cohorts={cohorts} navigate={navigate} />
        )}

        {/* Audio Exports Tab */}
        {activeTab === 'audioExports' && (
          <TrainingAudioExportQueue />
        )}

        {/* Sessions Tab */}
        {activeTab === 'sessions' && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'student'}
                      direction={sortField === 'student' ? sortDirection : 'asc'}
                      onClick={() => handleSort('student')}
                    >
                      <Typography variant="subtitle2" fontWeight={600}>Student</Typography>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'scenario'}
                      direction={sortField === 'scenario' ? sortDirection : 'asc'}
                      onClick={() => handleSort('scenario')}
                    >
                      <Typography variant="subtitle2" fontWeight={600}>Scenario</Typography>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="subtitle2" fontWeight={600}>Status</Typography>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'date'}
                      direction={sortField === 'date' ? sortDirection : 'desc'}
                      onClick={() => handleSort('date')}
                    >
                      <Typography variant="subtitle2" fontWeight={600}>Date</Typography>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={sortField === 'score'}
                      direction={sortField === 'score' ? sortDirection : 'desc'}
                      onClick={() => handleSort('score')}
                    >
                      <Typography variant="subtitle2" fontWeight={600}>Score</Typography>
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSessions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        {searchQuery || scenarioFilter || statusFilter
                          ? 'No sessions match your filters.'
                          : 'No session data available yet.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSessions.map((session) => (
                    <TableRow
                      key={session._id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleSessionClick(session._id)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {session.studentName || 'Unknown'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{session.scenarioName || '--'}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={getStatusLabel(session.status)}
                          size="small"
                          color={getStatusColor(session.status)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDateTime(session.completedAt || session.startedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {session.compositeScore != null ? (
                          <Chip
                            label={`${Math.round(session.compositeScore)}%`}
                            size="small"
                            color={session.compositeScore >= 70 ? 'success' : session.compositeScore >= 50 ? 'warning' : 'error'}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">--</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={sessionsPagination.total}
              page={sessionsPage}
              onPageChange={(e, newPage) => setSessionsPage(newPage)}
              rowsPerPage={sessionsPerPage}
              onRowsPerPageChange={(e) => { setSessionsPerPage(parseInt(e.target.value, 10)); setSessionsPage(0); }}
              rowsPerPageOptions={[10, 20, 50]}
            />
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default LecturerDashboard;
